import { useState, useRef, useCallback } from 'react';

const MIN_SUBMISSION_TIME_MS = 3000;

/**
 * Anti-bot hook providing honeypot field + submission timer.
 * - Honeypot: hidden field that bots auto-fill but humans never see.
 * - Timer: blocks submissions faster than 3 seconds (bot speed).
 *
 * Usage:
 *   const { honeypotProps, getAntiBotPayload, validateSubmission } = useAntiBot();
 *   // Render honeypotProps.field in the form (hidden from humans via CSS)
 *   // Call validateSubmission() before submitting — returns { ok, error }
 *   // Merge getAntiBotPayload() into your POST body
 */
export default function useAntiBot() {
  const [honeypot, setHoneypot] = useState('');
  const loadedAt = useRef(Date.now());

  const validateSubmission = useCallback(() => {
    if (honeypot) {
      // Bot filled the hidden field — silently "succeed" to not reveal detection
      return { ok: false, silent: true };
    }
    const elapsed = Date.now() - loadedAt.current;
    if (elapsed < MIN_SUBMISSION_TIME_MS) {
      return {
        ok: false,
        silent: false,
        error: 'Please take a moment to review your details before submitting.',
      };
    }
    return { ok: true };
  }, [honeypot]);

  const getAntiBotPayload = useCallback(() => ({
    _hp: honeypot,
    _t: loadedAt.current,
  }), [honeypot]);

  const honeypotProps = {
    field: (
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: 0,
          height: 0,
          overflow: 'hidden',
          tabIndex: -1,
        }}
      >
        <label htmlFor="website_url">Website</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
    ),
  };

  return { honeypotProps, getAntiBotPayload, validateSubmission };
}
